export const LAB_STATUS = {
  PENDING_VERIFICATION: "PENDING_VERIFICATION",
  RESUBMISSION_REQUIRED: "RESUBMISSION_REQUIRED",
  ACTIVE: "ACTIVE",
  REJECTED: "REJECTED",
};

export const LAB_STATUS_LABELS = {
  PENDING_VERIFICATION: "Pending Verification",
  RESUBMISSION_REQUIRED: "Resubmission Required",
  ACTIVE: "Active",
  REJECTED: "Rejected",
};

export const LAB_STATUS_COLORS = {
  PENDING_VERIFICATION: { bgcolor: "#FEF3C7", color: "#B45309" },
  RESUBMISSION_REQUIRED: { bgcolor: "#EEF2FF", color: "#4338CA" },
  ACTIVE: { bgcolor: "#ECFDF5", color: "#047857" },
  REJECTED: { bgcolor: "#FEF2F2", color: "#B91C1C" },
};

export const LAB_STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  ...Object.keys(LAB_STATUS_LABELS).map((value) => ({
    value,
    label: LAB_STATUS_LABELS[value],
  })),
];

export const HISTORY_ACTION_LABELS = {
  REGISTER: "Registered",
  APPROVE: "Approved & activated",
  REJECT: "Rejected",
  RETURN_FOR_CORRECTION: "Returned for correction",
  RESUBMIT: "Resubmitted for verification",
};

export const LAB_LIMITS = {
  nameMax: 100,
  ownerNameMax: 100,
  emailMax: 100,
  addressMax: 500,
  stateMax: 50,
  districtMax: 100,
  cityMax: 100,
  hfrIdMax: 50,
  reasonMin: 3,
  reasonMax: 1000,
  documentsMax: 5,
  documentSizeBytes: 5 * 1024 * 1024,
};

export const DOCUMENT_ACCEPT = ".jpg,.jpeg,.png,.pdf";
const DOCUMENT_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

export const normalizeLabContactNumber = (value) => {
  let digits = String(value ?? "")
    .normalize("NFKC")
    .replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return /^\d{10}$/.test(digits) ? digits : null;
};

const tidy = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

export const EMPTY_LAB_FORM = {
  labName: "",
  ownerName: "",
  contactNumber: "",
  email: "",
  hfrId: "",
  state: "",
  district: "",
  city: "",
  address: "",
  pincode: "",
};

export const toLabForm = (lab) =>
  Object.keys(EMPTY_LAB_FORM).reduce(
    (form, key) => ({ ...form, [key]: lab?.[key] ?? "" }),
    {},
  );

export const validateLabForm = (values) => {
  const errors = {};
  const labName = tidy(values?.labName);
  const ownerName = tidy(values?.ownerName);
  const contact = tidy(values?.contactNumber);
  const email = tidy(values?.email);
  const hfrId = tidy(values?.hfrId);
  const pincode = tidy(values?.pincode);

  if (!labName) errors.labName = "Lab Name is required";
  else if (labName.length > LAB_LIMITS.nameMax)
    errors.labName = `Lab Name must be at most ${LAB_LIMITS.nameMax} characters`;
  else if (!/^[a-zA-Z0-9 ()\-_.&@+,'/]*$/.test(labName))
    errors.labName = "Lab Name contains characters that are not allowed";

  if (!ownerName) errors.ownerName = "Lab Owner Name is required";
  else if (ownerName.length > LAB_LIMITS.ownerNameMax)
    errors.ownerName = `Lab Owner Name must be at most ${LAB_LIMITS.ownerNameMax} characters`;
  else if (!/^[a-zA-Z .'-]*$/.test(ownerName))
    errors.ownerName =
      "Lab Owner Name may only contain letters, spaces, dots, apostrophes and hyphens";

  if (!contact) errors.contactNumber = "Contact Number is required";
  else if (!normalizeLabContactNumber(contact))
    errors.contactNumber = "Enter a valid 10-digit Contact Number";

  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    errors.email = "Enter a valid email address";
  if (tidy(values?.address).length > LAB_LIMITS.addressMax)
    errors.address = `Address must be at most ${LAB_LIMITS.addressMax} characters`;
  if (tidy(values?.district).length > LAB_LIMITS.districtMax)
    errors.district = `District must be at most ${LAB_LIMITS.districtMax} characters`;
  if (tidy(values?.city).length > LAB_LIMITS.cityMax)
    errors.city = `City must be at most ${LAB_LIMITS.cityMax} characters`;
  if (pincode && !/^[1-9][0-9]{5}$/.test(pincode))
    errors.pincode = "Enter a valid 6-digit PIN Code";
  if (hfrId && !/^[A-Za-z0-9-]{1,50}$/.test(hfrId))
    errors.hfrId = "HFR ID may only contain letters, digits and hyphens";

  return errors;
};

export const toLabPayload = (values) =>
  Object.keys(EMPTY_LAB_FORM).reduce(
    (payload, key) => ({ ...payload, [key]: tidy(values?.[key]) }),
    {},
  );

export const reasonError = (reason) => {
  const text = tidy(reason);
  if (!text) return "A reason is required";
  if (text.length < LAB_LIMITS.reasonMin)
    return `The reason must be at least ${LAB_LIMITS.reasonMin} characters`;
  if (text.length > LAB_LIMITS.reasonMax)
    return `The reason must be at most ${LAB_LIMITS.reasonMax} characters`;
  return "";
};

export const documentFileError = (file, currentCount = 0) => {
  if (!file) return "Choose a file";
  if (currentCount >= LAB_LIMITS.documentsMax)
    return `At most ${LAB_LIMITS.documentsMax} supporting documents can be attached`;
  if (!DOCUMENT_MIME_TYPES.includes(file.type))
    return "Invalid format. Only JPG, JPEG, PNG and PDF allowed.";
  if (file.size > LAB_LIMITS.documentSizeBytes)
    return "The file must be 5 MB or smaller";
  return "";
};

export const labActions = (status) => ({
  canEdit: [
    LAB_STATUS.PENDING_VERIFICATION,
    LAB_STATUS.RESUBMISSION_REQUIRED,
    LAB_STATUS.ACTIVE,
  ].includes(status),
  canApprove: status === LAB_STATUS.PENDING_VERIFICATION,
  canReturnForCorrection: status === LAB_STATUS.PENDING_VERIFICATION,
  canReject: [
    LAB_STATUS.PENDING_VERIFICATION,
    LAB_STATUS.RESUBMISSION_REQUIRED,
  ].includes(status),
  canResubmit: status === LAB_STATUS.RESUBMISSION_REQUIRED,
  canMap: status === LAB_STATUS.ACTIVE,
  canRemoveDocuments: [
    LAB_STATUS.PENDING_VERIFICATION,
    LAB_STATUS.RESUBMISSION_REQUIRED,
  ].includes(status),
});

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
